import React from 'react';

import { Box } from '@opendoor/bricks/core';

// Aspect ratio (5:3) expressed as the height as a percentage of the width
const TARGET_ASPECT_RATIO_PCT = '60%';

interface PropertyCardImageContainerProps {
  children: React.ReactNode;
  fullWidth?: boolean;
}

/**
 * Renders a container for property card images with rounded corners and the height
 * necessary to achieve the desired image aspect ratio
 *
 * This is accomplished with pure CSS using the methodology from
 * https://stackoverflow.com/a/10441480
 *
 * Content rendered in this container should be given a width and height of 100%. For
 * image tags, make sure to specify `object-fit: cover` to prevent distortion, as the
 * source image aspect ratios will usually not be identical to the aspect ratio of this
 * container.
 *
 * storybook-check-ignore
 */
export const PropertyCardImageContainer: React.FC<PropertyCardImageContainerProps> = ({
  children,
  fullWidth,
}) => {
  return (
    <Box
      position="relative"
      pb={fullWidth ? undefined : TARGET_ASPECT_RATIO_PCT}
      height={fullWidth ? '180px' : undefined}
      borderRadius={fullWidth ? 'none' : 'roundedSquare'}
      borderBottomLeftRadius="none"
      borderBottomRightRadius="none"
      overflow="hidden"
      transform="translateZ(0)" // Workaround for Safari bug: https://stackoverflow.com/a/58283449
      data-testid="property-card-image-container"
    >
      <Box position="absolute" width="100%" height="100%">
        {children}
      </Box>
    </Box>
  );
};
